import { Link, useParams } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import FormInput from "../components/inputs/FormInput.tsx";
import { InputType } from "../types/input.ts";
import ButtonLink from "../components/buttons/ButtonLink.tsx";
import { ButtonSize, ButtonType, ButtonVariant } from "../types/button.ts";
import Button from "../components/buttons/Button.tsx";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import ValidatorsHelper from "../helpers/validatorsHelper.ts";
import apiNet from "../newApi/apiNet.ts";
import axios, { AxiosError } from "axios";
import { NotificationStatus } from "../components/notification/Notification.tsx";
import { useNotification } from "../providers/NotificationProvider.tsx";
import FormWrapper from "../components/forms/FormWrapper.tsx";
import UtilsHelper from "../helpers/utilsHelper.ts";
import CircleLoader from "../components/loaders/CircleLoader.tsx";
import { IAxiosErrorResponse } from "../interfaces/IAxiosErrorResponse.ts";

const RegisterPage = () => {
    const _notificationService = useNotification();
    const { token } = useParams();
    const [verificationToken, setVerificationToken] = useState<string | undefined | null>(undefined);
    const [showErrors, setShowErrors] = useState(false);
    const [isFinishedSuccess, setIsFinishedSuccess] = useState(false);
    const [input, setInput] = useState({
        email: "",
        userName: "",
        password: "",
        confirmPassword: "",
        isTermsAccepted: false
    });

    const [inputValid, setInputValid] = useState({
        emailIsValid: false,
        userNameIsValid: false,
        passwordIsValid: false,
        confirmPasswordIsValid: false,
        isTermsAcceptedIsValid: false,
    })
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmitEvent = (e: FormEvent) => {
        e.preventDefault();
        (async () => {
            if (inputValid.passwordIsValid && inputValid.confirmPasswordIsValid && verificationToken && inputValid.isTermsAcceptedIsValid) {
                try {
                    setIsLoading(true);
                    await apiNet.register(input.email, input.password, input.confirmPassword, verificationToken);
                    setIsLoading(false);
                    setIsFinishedSuccess(true);
                    _notificationService.showNotification("Sukces", "Rejestracja powiodła się. Na podany email wysłaliśmy link aktywacyjny. Kliknij w niego aby dokończyć rejestrację.", NotificationStatus.success)
                } catch (e: unknown) {
                    setIsLoading(false);
                    const error = e as AxiosError<IAxiosErrorResponse>;
                    if (axios.isAxiosError(error) && error.response) {
                        if (error.response.data.MsgCode === "INVALID_TOKEN") {
                            _notificationService.showNotification("Niepowdzenie", "Link rejestracyjny jest nieprawidłowy. Przekierowuje na stronę BOK. Proszę ponownie kliknąć w link rejestracyjny.", NotificationStatus.failure)
                        } else if (error.response.data.MsgCode === 'USER_EXIST')
                            _notificationService.showNotification("Niepowdzenie", "Adres email jest zajęty.", NotificationStatus.failure);
                    }
                }
            }
            else {
                setShowErrors(true);
            }
        })();
    }

    const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput((prev) => ({
            ...prev,
            email: e.target.value
        }));
        setInputValid(prevState => ({
            ...prevState,
            emailIsValid: ValidatorsHelper.isEmailValid(e.target.value)
        }));
    }

    const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput((prev) => ({
            ...prev,
            password: e.target.value
        }));
        setInputValid(prevState => ({
            ...prevState,
            passwordIsValid: ValidatorsHelper.isPasswordValid(e.target.value),
            confirmPasswordIsValid: e.target.value === input.confirmPassword
        }));
    }

    const handleConfirmPasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput((prev) => ({
            ...prev,
            confirmPassword: e.target.value
        }));
        setInputValid(prevState => ({
            ...prevState,
            passwordIsValid: ValidatorsHelper.isPasswordValid(input.password),
            confirmPasswordIsValid: e.target.value === input.password
        }));
    }

    const handleIsTermsAccepted = (e: ChangeEvent<HTMLInputElement>) => {
        setInput((prev) => ({
            ...prev,
            isTermsAccepted: e.target.checked
        }));
        setInputValid(prevState => ({
            ...prevState,
            isTermsAcceptedIsValid: e.target.checked
        }));
    }

    useEffect(() => {
        UtilsHelper.changeWindowDocumentTitle("Rejestracja");
        (async () => {
            try {
                if (token) {
                    const result = await apiNet.verifyRegisterToken(token);
                    if (result.status === 204)
                        setVerificationToken(token);
                }
            } catch (e) {
                const error = e as Error | AxiosError;
                if (axios.isAxiosError(error) && error.response && error.response.status === 401) {
                    setVerificationToken(null);
                }
            }
        })()
    }, [token]);

    return (
        <div className={"flex flex-col min-h-screen justify-between items-center text-neutral-100 px-2 text-center bg-neutral-800 md:bg-neutral-800"}>
            <Link to={"/"}>
                <LazyLoadImage
                    alt=""
                    className="object-cover auto min-h-full h-16 md:h-14 my-4"
                    effect="opacity"
                    src={`/images/logofull.png`}
                />
            </Link>
            {verificationToken && !isFinishedSuccess &&
                <FormWrapper>
                    <h1 className={"text-3xl font-medium"}>Tworzenie konta</h1>
                    <p className={"mt-5 text-neutral-300"}>Wypełnij oraz wyślij poniższy formularz, aby utworzyć nowe konto</p>
                    <form
                        action="/recovery-password"
                        className={"w-full mt-10 text-left"}
                        onSubmit={(e) => handleSubmitEvent(e)}
                    >
                        <span className={"space-y-2"}>
                            <FormInput
                                label={"Email"}
                                name={"confirmPassword"}
                                onInputChange={handleEmailInputChange}
                                type={InputType.EMAIL}
                                value={input.email}
                                placeholder={"Wpisz adres email"}
                                className={"py-2 placeholder:text-neutral-400 ring-offset-neutral-900 placeholder:font-light bg-neutral-700"}
                            />
                            {showErrors && !inputValid.emailIsValid &&
                                <p className={"text-red-500"}>Adres email jest nieprawidłowy</p>}
                            <FormInput
                                label={"Hasło"}
                                name={"password"}
                                onInputChange={handlePasswordInputChange}
                                type={InputType.PASSWORD}
                                value={input.password}
                                placeholder={"Wpisz hasło"}
                                className={"py-2 placeholder:text-neutral-400 ring-offset-neutral-900 placeholder:font-light bg-neutral-700"}
                            />
                            {showErrors && !inputValid.passwordIsValid &&
                                <p className={"text-red-500 font-light"}>
                                    <span className={"font-normal"}>Hasło musi zawierać:</span> <br />
                                    - minimum 8 znaków <br />
                                    - znak specjalny <br />
                                    - dużą literę <br />
                                    - cyfrę</p>
                            }
                            <FormInput
                                label={"Potwierdź hasło"}
                                name={"confirmPassword"}
                                onInputChange={handleConfirmPasswordInputChange}
                                type={InputType.PASSWORD}
                                value={input.confirmPassword}
                                placeholder={"Potwierdź hasło"}
                                className={"py-2 placeholder:text-neutral-400 ring-offset-neutral-900 placeholder:font-light bg-neutral-700"}
                            />
                            {showErrors && !inputValid.confirmPasswordIsValid &&
                                <p className={"text-red-500"}>Hasło i potwierdź hasło różnią się od siebie</p>}
                        </span>
                        <label
                            htmlFor="termsAccepted"
                            className={"relative flex mt-4 items-center select-none"}
                        >
                            <input
                                type="checkbox"
                                name="termsAccepted"
                                id="termsAccepted"
                                checked={input.isTermsAccepted}
                                onChange={handleIsTermsAccepted}
                                className={"relative appearance-none peer shrink-0 rounded-full overflow-hidden w-5 h-5 outline-hidden ring-transparent ring-2 ring-offset-2 border border-neutral-100 ring-offset-neutral-900 checked:bg-[#008DD2] checked:border-0"}
                            />
                            <p className={"ml-2 text-sm text-neutral-200 font-medium mt-0.5"}>Oświadczam, że znam i
                                akcteptuję postanowanie
                                <ButtonLink to={"/regulamin"} targetType={"_self"} variant={ButtonVariant.SECONDARY}
                                    className={"ml-1 pl-0 pr-0 pt-0 pb-0 font-bold"}>regulaminu</ButtonLink></p>
                            <svg
                                className="absolute w-4 h-4 ml-0.5 mt-0.5 hidden peer-checked:block text-neutral-100"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                        </label>
                        {showErrors && !inputValid.isTermsAcceptedIsValid &&
                            <p className={"text-red-500 mt-2"}>To pole jest wymagane</p>}
                        <Button
                            type={ButtonType.SUBMIT}
                            size={ButtonSize.BASE}
                            variant={ButtonVariant.PRIMARY}
                            disabled={isLoading}
                            className={"flex items-center justify-center mx-auto block mt-4 w-full py-1.5 text-lg ring-offset-neutral-900"}>
                            {isLoading ?
                                <>
                                    <CircleLoader />
                                    <span className="ml-2">Tworzenie konta</span>
                                </>
                                :
                                "Utwórz konto"
                            }
                        </Button>
                    </form>
                </FormWrapper>}
            {verificationToken === null && !isFinishedSuccess &&
                <FormWrapper>
                    <XCircleIcon className={"w-16 h-16 text-red-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Link nieprawidłowy</h1>
                    <p className={"text-neutral-400"}>Link jest nieprawidłowy lub wygasł. Proszę przejść na
                        stronę<ButtonLink to={import.meta.env.VITE_MIX_LINK_TO_LMS} targetType={"_blank"}
                            variant={ButtonVariant.SECONDARY}
                            className={"ml-1 pl-0 pr-0 pt-0 pb-0 font-bold"}>biura obsługi
                            klienta</ButtonLink> i ponownie przejść na stronę rejestracji.</p>
                </FormWrapper>
            }
            {verificationToken === undefined && !isFinishedSuccess &&
                <section className={"self-center my-5 max-w-xs"}>
                    <svg
                        className={"mx-auto mt-16"}
                        width="44"
                        height="44"
                        viewBox="0 0 44 44"
                        xmlns="http://www.w3.org/2000/svg"
                        stroke="#fff"
                    >
                        <g fill="none" fillRule="evenodd" strokeWidth="2">
                            <circle cx="22" cy="22" r="1">
                                <animate
                                    attributeName="r"
                                    begin="0s"
                                    dur="1.8s"
                                    values="1; 20"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.165, 0.84, 0.44, 1"
                                    repeatCount="indefinite" />
                                <animate
                                    attributeName="stroke-opacity"
                                    begin="0s"
                                    dur="1.8s"
                                    values="1; 0"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.3, 0.61, 0.355, 1"
                                    repeatCount="indefinite" />
                            </circle>
                            <circle cx="22" cy="22" r="1">
                                <animate
                                    attributeName="r"
                                    begin="-0.9s"
                                    dur="1.8s"
                                    values="1; 20"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.165, 0.84, 0.44, 1"
                                    repeatCount="indefinite" />
                                <animate
                                    attributeName="stroke-opacity"
                                    begin="-0.9s"
                                    dur="1.8s"
                                    values="1; 0"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.3, 0.61, 0.355, 1"
                                    repeatCount="indefinite" />
                            </circle>
                        </g>
                    </svg>
                </section>
            }
            {isFinishedSuccess &&
                <FormWrapper>
                    <CheckCircleIcon className={"w-16 h-16 text-green-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Rejestracja zakończona pomyślnie</h1>
                    <p className={"text-neutral-400"}>Na adres email wysłaliśmy potwierdzenie rejestracji z linkiem
                        aktywacyjnym. Proszę kliknąć w link aktywacyjny w wiadomości aby dokończyć rejestrację.</p>
                </FormWrapper>
            }
            <div className={"min-h-14"} />
        </div>
    );
};

export default RegisterPage;